import React from 'react';
import DOMPurify from 'dompurify';

/**
 * Combine multiple strings containing class names into a single string,
 * removing duplicates. E.g.
 * cleanClassNames('bar', 'baz bar', 'x y ', undefined)
 * // 'bar baz x y'
 */
export function cleanClassNames(classNameArr, omit = []) {
  // Split each arg on whitespace, and add it to an array. Skip false-y args
  // like "" and undefined.
  const classList = classNameArr
    .filter(Boolean)
    .reduce(
      (previous, current) => previous.concat(current.trim().split(/\s+/)),
      []
    );

  // Remove any class names from omit, and make the rest unique before
  // returning them as a string
  return [...new Set(classList.filter(cn => !omit.includes(cn)))].join(' ');
}

/**
 * Extract props meant for semantic UI components from props that are
 * passed to Widgets, Templates and Fields.
 */
export function getSemanticProps({
  formContext = {},
  uiSchema = {},
  options = {},
  defaultSchemaProps = { fluid: true, inverted: false },
  defaultContextProps = {},
}) {
  const formContextProps = formContext.semantic;
  let schemaProps = uiSchema['ui:options'] && uiSchema['ui:options'].semantic;
  let optionProps = options.semantic;
  // formContext props should overide other props
  return Object.assign(
    {},
    { ...(defaultSchemaProps && defaultSchemaProps) },
    { ...(defaultContextProps && defaultContextProps) },
    schemaProps,
    optionProps,
    formContextProps
  );
}

export function MaybeWrap({ wrap, component: Component = 'div', ...props }) {
  return wrap ? <Component {...props} /> : props.children;
}

export function isEmptyHtml(htmlString) {
  if (typeof htmlString !== 'string') return true;
  const temp = document.createElement('div');
  temp.innerHTML = htmlString;
  return temp.textContent.trim() === '';
}

export function validateTransactions(evidenceSource, questionData) {
  if (
    evidenceSource !== 'Transaction Testing' ||
    !questionData ||
    !Array.isArray(questionData.transactions)
  ) {
    return { valid: true, errors: [] };
  }
  const errors = [];

  const hasMeaningfulText = inputHTML => {
    const cleanHTML = DOMPurify.sanitize(inputHTML);
    const plainText = cleanHTML
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim();

    return plainText.length > 0;
  };
  const validateCategory = (
    catValue,
    otherDesc,
    fieldName,
    transaction,
    mandatory = true
  ) => {
    if (!catValue) {
      if (mandatory) errors.push(`${fieldName} is missing`);
      return !mandatory;
    }
    const val = catValue.toString();
    if (['1', '2', '3', '4'].includes(val)) return true;

    if (val === '5') {
      if (!hasMeaningfulText(transaction[otherDesc])) {
        errors.push(`${fieldName} requires ${otherDesc}`);
        return false;
      }
    }
    return true;
  };

  const validateSubCategory = (
    subCatValue,
    otherDesc,
    fieldName,
    transaction,
    mandatory = true
  ) => {
    if (!subCatValue) {
      if (mandatory) errors.push(`${fieldName} is missing`);
      return !mandatory;
    }
    const num = subCatValue.toString();
    if (Array.from({ length: 12 }, (_, i) => (i + 1).toString()).includes(num))
      return true;

    if (num === '13') {
      if (!hasMeaningfulText(transaction[otherDesc])) {
        errors.push(`${fieldName} requires ${otherDesc}`);
        return false;
      }
    }
    return true;
  };

  questionData.transactions.forEach((txn, index) => {
    validateCategory(
      txn.txnCategory1,
      'otherDescTxnCategory1',
      `txnCategory1 (txn #${index + 1})`,
      txn,
      true
    );
    validateSubCategory(
      txn.txnSubCategory1,
      'otherDescTxnSubCategory1',
      `txnSubCategory1 (txn #${index + 1})`,
      txn,
      true
    );
    validateCategory(
      txn.txnCategory2,
      'otherDescTxnCategory2',
      `txnCategory2 (txn #${index + 1})`,
      txn,
      false
    );
    validateSubCategory(
      txn.txnSubCategory2,
      'otherDescTxnSubCategory2',
      `txnSubCategory2 (txn #${index + 1})`,
      txn,
      false
    );
  });

  return { valid: errors.length === 0, errors };
}

export function cleanTransactions(obj) {
  if (!obj?.transactions) return;
  Object.values(obj.transactions).forEach(txn => {
    if (txn && typeof txn === 'object') {
      delete txn.otherDescTxnCategory1;
      delete txn.otherDescTxnCategory2;
      delete txn.otherDescTxnSubCategory1;
      delete txn.otherDescTxnSubCategory2;
      delete txn.__errors;
    }
  });
}
